import { ReactElement } from 'react';
import { BaseAudit, FullProduct } from 'types/domainTypes';
import { Grid, Stack } from '@mui/material';
import { LabelledText } from 'components/LabelledText/LabelledText';
import { Panel } from 'components/Panel/Panel';
import { useTranslation } from 'react-i18next';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { EditOutlined } from '@mui/icons-material';

interface ProductDetailsViewProps {
  productDetails: FullProduct & BaseAudit;
  onEditClick: () => void;
}

export function ProductDetailsView({ productDetails, onEditClick }: ProductDetailsViewProps): ReactElement {
  const { t, i18n } = useTranslation();
  const {
    division,
    panelType,
    panelThickness,
    externalProfile,
    externalFaceThickness,
    externalGrade,
    internalProfile,
    internalFaceThickness,
    internalGrade,
    panelWidthForFixingCalculations,
    selfWeight,
  } = productDetails;

  const formatDecimal = (number: number) => {
    return number.toLocaleString(i18n.language, { maximumFractionDigits: 3, minimumFractionDigits: 3 });
  };

  return (
    <Panel
      data-testid="product-panel"
      panelTitle={t('calculation-page.label-product')}
      panelSummary={t('common.summary-format', {
        createdBy: productDetails.createdByName ? productDetails.createdByName : productDetails.createdBy,
        modifiedOn: productDetails.modifiedOn,
        modifiedBy: productDetails.modifiedByName ? productDetails.modifiedByName : productDetails.modifiedBy,
      })}
      panelButton={
        <ShrinkToIconButton buttonText={t('calculation-page.edit-product-button')} icon={<EditOutlined />} onClick={onEditClick} />
      }
    >
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="calculation-page.label-division" value={division} />
          <LabelledText label="calculation-page.label-panel-type" value={panelType} />
          <LabelledText label="calculation-page.label-panel-thickness" value={String(panelThickness)} />
          <LabelledText label="calculation-page.label-cover-width" value={String(panelWidthForFixingCalculations)} />
        </Stack>
      </Grid>
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="calculation-page.label-external-profile" value={externalProfile} />
          <LabelledText label="calculation-page.label-external-face-thickness" value={formatDecimal(externalFaceThickness)} />
          <LabelledText label="calculation-page.label-external-grade" value={externalGrade} />
          <LabelledText label="calculation-page.label-self-weight" value={String(selfWeight)} />
        </Stack>
      </Grid>
      <Grid item xs={4}>
        <Stack spacing={2}>
          <LabelledText label="calculation-page.label-internal-profile" value={internalProfile} />
          <LabelledText label="calculation-page.label-internal-face-thickness" value={formatDecimal(internalFaceThickness)} />
          <LabelledText label="calculation-page.label-internal-grade" value={internalGrade} />
        </Stack>
      </Grid>
    </Panel>
  );
}
